/* eslint-disable no-unused-expressions */
/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/button-has-type */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';

import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import { get } from 'lodash';

import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { isPromoCodeApplied } from '../../util/transaction';
import { ensurePaymentMethodCard } from '../../util/data';
import { isVerified } from '../../util/user';
import { composeValidators, required, maxLength } from '../../util/validators';
import * as log from '../../util/log';

import {
  Form,
  FieldGuestLiabilityProtection,
  FieldTextInput,
  FieldPromoCode,
  FieldTripInsurance,
  CredovaPlugin,
  SavedCardDetails,
  IDVerification,
  PageSlider,
} from '..';

import css from './StripeCheckoutForm.module.css';
import StripePaymentElement from './StripePaymentElement';
import { Divider, Button } from '@landtrustinc/design-system';
import { convertMoneyToNumber } from '../../util/currency';
import { isGlpLtPlusSelection, normalizeGlpSelection } from '../../util/guestLiabilityProtection';
import { createAndConfirmLtPlusSubscription } from '../../util/ltPlusSubscription';
import { hasPremiumMembership } from '../../util/user';
import { track, SUCCESSFUL_SUBSCRIPTION } from '../../tracking';
import { formatLtPlusMonthlyPrice } from '../../util/landtrustPlus';
import {
  CHECKOUT_SUBMIT_PHASE,
  isCheckoutSubmitInProgress,
} from '../../util/checkoutSubmitProgress';
import CheckoutSubmitOverlay from './CheckoutSubmitOverlay';

const MAX_MESSAGE_LENGTH = 1000;

const getPaymentMethod = (selectedPaymentMethod, hasDefaultPaymentMethod) => {
  return selectedPaymentMethod == null && hasDefaultPaymentMethod
    ? 'defaultCard'
    : selectedPaymentMethod == null
    ? 'onetimeCardPayment'
    : selectedPaymentMethod;
};

const getStripeConfirmErrorMessage = (error, intl) => {
  if (error?.code === 'payment_intent_unexpected_state') {
    return intl.formatMessage({ id: 'StripePaymentForm.paymentAlreadyAuthorizedError' });
  }

  // Only card and validation errors carry text written for the customer. Anything else (for
  // example an invalid request) can name internal ids, so show the generic message instead.
  if (error?.type === 'card_error' || error?.type === 'validation_error') {
    return error.message || intl.formatMessage({ id: 'StripePaymentForm.genericError' });
  }

  return intl.formatMessage({ id: 'StripePaymentForm.genericError' });
};

const PaymentMethodSelector = props => {
  const { changePaymentMethod, defaultPaymentMethod } = props;

  return (
    <>
      <SavedCardDetails
        className={css.paymentMethodSelector}
        card={defaultPaymentMethod.attributes.card}
        onChange={changePaymentMethod}
      />
    </>
  );
};

export const PaymentForm = ({ intl: _, label, showSaveCardCheckbox }) => {
  return (
    <StripePaymentElement
      label={label}
      showSaveCardCheckbox={showSaveCardCheckbox}
      paymentElementId="payment-element"
    />
  );
};

const StripeCheckoutForm = ({
  onSubmit,
  submitClassName,
  showInitialMessageInput,
  intl,
  authorDisplayName,
  transaction,
  onApplyPromoCode,
  onApplyInsurance,
  onApplyGuestLiabilityProtection,
  onCompleteLtPlusSubscription,
  onBeginLtPlusSignup,
  onEndLtPlusSignup,
  guestLiabilityProtection,
  tripInsurance,
  isGuestLiabilityProtectionLocked,
  defaultPaymentMethod,
  clientSecret,
  isSubmitting,
  setIsSubmitting,
  buttonLabel,
  usedPromoCode,
  fetchUser,
  showInsuranceForm,
  showGuestLiabilityProtectionForm,
  listing,
  followSubjectMutation,
  user,
  bookingBreakdown,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [paymentMessage, setPaymentMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod?.id ? 'defaultCard' : '');
  const [showSaveCardCheckbox, setShowSaveCardCheckbox] = useState(true);
  const [showMissingInformationReminder, setShowMissingInformationReminder] = useState(false);
  const [isApplyingInsurance, setIsApplyingInsurance] = useState(false);
  const [isApplyingGuestLiabilityProtection, setIsApplyingGuestLiabilityProtection] = useState(
    false
  );
  const [submitProgressPhase, setSubmitProgressPhase] = useState(CHECKOUT_SUBMIT_PHASE.IDLE);
  const [includeLtPlusSubmitFlow, setIncludeLtPlusSubmitFlow] = useState(false);
  const submitProgressPhaseRef = useRef(CHECKOUT_SUBMIT_PHASE.IDLE);
  const submittedFormValuesRef = useRef(null);

  useEffect(() => {
    submitProgressPhaseRef.current = submitProgressPhase;
  }, [submitProgressPhase]);

  useEffect(() => {
    if (!isSubmitting && isCheckoutSubmitInProgress(submitProgressPhase)) {
      resetSubmitProgress();
    }
  }, [isSubmitting, submitProgressPhase]);

  const resetSubmitProgress = () => {
    setSubmitProgressPhase(CHECKOUT_SUBMIT_PHASE.IDLE);
    setIncludeLtPlusSubmitFlow(false);
  };

  const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(defaultPaymentMethod);

  const handlePaymentMethodChange = event => {
    const { value } = event;

    if (value.type !== 'card') {
      setShowSaveCardCheckbox(false);
    } else {
      setShowSaveCardCheckbox(true);
    }
  };

  useEffect(() => {
    if (elements) {
      const paymentElement = elements.getElement(PaymentElement);

      if (paymentElement) {
        paymentElement.on('change', handlePaymentMethodChange);
      }
    }
  }, [elements, paymentMethod]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const existingClientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!existingClientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(existingClientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          setPaymentMessage('Payment succeeded!');
          break;
        case 'processing':
          setPaymentMessage('Your payment is processing.');
          break;
        case 'requires_payment_method':
          setErrorMessage('Your payment was not successful, please try again.');
          break;
        default:
          setErrorMessage('Something went wrong.');
          break;
      }
    });
  }, [stripe]);

  const handleUserVerification = async currentUser => {
    if (!isVerified(currentUser)) {
      setShowMissingInformationReminder(true);
      setIsSubmitting(false);
      resetSubmitProgress();

      return false;
    }

    return true;
  };

  const handleFormSubmit = async values => {
    submittedFormValuesRef.current = values;
    setIsSubmitting(true);

    let currentUser = await fetchUser();
    const isUserVerified = await handleUserVerification(currentUser);

    if (!isUserVerified) {
      resetSubmitProgress();
      return;
    }

    const stripePaymentMethodId = ensuredDefaultPaymentMethod.id
      ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
      : null;

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      setIsSubmitting(false);
      resetSubmitProgress();
      return;
    }

    setErrorMessage(null);

    const { initialMessage, saveAfterOnetimePayment, guestLiabilityProtection: formGlpSelection } =
      submittedFormValuesRef.current || values;

    const profile = get(currentUser, 'attributes.profile', {});
    const email = get(currentUser, 'attributes.email');
    const { firstName, lastName } = profile;
    const billingDetails = {};

    if (email) {
      billingDetails.email = email;
    }

    if (firstName && lastName) {
      billingDetails.name = `${firstName} ${lastName}`;
    }

    const resolvedPaymentMethod = getPaymentMethod(
      paymentMethod,
      ensurePaymentMethodCard(defaultPaymentMethod).id
    );

    let bookingClientSecret = clientSecret;

    const glpSelection = normalizeGlpSelection(formGlpSelection ?? guestLiabilityProtection);
    const includesLtPlusSignup =
      isGlpLtPlusSelection(glpSelection) &&
      !hasPremiumMembership(currentUser) &&
      onCompleteLtPlusSubscription;

    if (includesLtPlusSignup && typeof onBeginLtPlusSignup === 'function') {
      onBeginLtPlusSignup();
    }

    setIncludeLtPlusSubmitFlow(includesLtPlusSignup);
    setSubmitProgressPhase(
      includesLtPlusSignup
        ? CHECKOUT_SUBMIT_PHASE.LT_PLUS
        : CHECKOUT_SUBMIT_PHASE.CONFIRMING_PAYMENT
    );

    try {
      let ltPlusSubscriptionId;

      if (includesLtPlusSignup) {
        try {
          const subscriptionResult = await createAndConfirmLtPlusSubscription({
            stripe,
            elements,
            paymentMethod: resolvedPaymentMethod,
            stripePaymentMethodId,
            billingDetails,
            onBeforeConfirm: () =>
              setSubmitProgressPhase(CHECKOUT_SUBMIT_PHASE.CONFIRMING_MEMBERSHIP),
          });
          ltPlusSubscriptionId = subscriptionResult?.subscriptionId;

          if (!subscriptionResult?.existingSubscription) {
            try {
              track(SUCCESSFUL_SUBSCRIPTION, currentUser);
            } catch (trackingError) {
              log.error(trackingError, 'tracking error', { event: SUCCESSFUL_SUBSCRIPTION });
            }
          }
        } catch (subscriptionError) {
          if (typeof onEndLtPlusSignup === 'function') {
            onEndLtPlusSignup();
          }
          setErrorMessage(
            subscriptionError.message ||
              intl.formatMessage({ id: 'StripePaymentForm.ltPlusSubscriptionError' })
          );
          setIsSubmitting(false);
          resetSubmitProgress();
          return;
        }

        setSubmitProgressPhase(CHECKOUT_SUBMIT_PHASE.REFRESHING_BOOKING);

        const {
          clientSecret: updatedClientSecret,
          user: ltPlusUser,
        } = await onCompleteLtPlusSubscription(ltPlusSubscriptionId);
        bookingClientSecret = updatedClientSecret;
        if (ltPlusUser) {
          currentUser = ltPlusUser;
        }
      }

      setSubmitProgressPhase(CHECKOUT_SUBMIT_PHASE.CONFIRMING_PAYMENT);

      if (resolvedPaymentMethod !== 'defaultCard') {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          if (typeof onEndLtPlusSignup === 'function') {
            onEndLtPlusSignup();
          }
          setErrorMessage(
            submitError.message || intl.formatMessage({ id: 'StripePaymentForm.genericError' })
          );
          setIsSubmitting(false);
          resetSubmitProgress();
          return;
        }
      }

      const confirmPayment =
        resolvedPaymentMethod === 'defaultCard'
          ? stripe.confirmCardPayment(bookingClientSecret, {
              payment_method: stripePaymentMethodId,
            })
          : stripe.confirmPayment({
              elements,
              clientSecret: bookingClientSecret,
              redirect: 'if_required',
              confirmParams: {
                payment_method_data: {
                  billing_details: billingDetails,
                },
              },
            });

      const { error, paymentIntent } = await confirmPayment;

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        setErrorMessage(getStripeConfirmErrorMessage(error, intl));
        log.error(
          new Error(`Stripe confirm failed: ${error.code || error.type}`),
          'stripe-confirm-failed',
          {
            stripeType: error.type,
            stripeCode: error.code,
            declineCode: error.decline_code,
            param: error.param,
            paymentIntentId: error.payment_intent?.id,
            paymentMethod: resolvedPaymentMethod,
          }
        );

        if (typeof onEndLtPlusSignup === 'function') {
          onEndLtPlusSignup();
        }
        setIsSubmitting(false);
        resetSubmitProgress();
      } else {
        setSubmitProgressPhase(CHECKOUT_SUBMIT_PHASE.SUBMITTING_ORDER);

        const params = {
          message: initialMessage ? initialMessage.trim() : null,
          paymentIntent,
          saveAfterOnetimePayment,
          paymentMethod: resolvedPaymentMethod,
        };

        onSubmit(params);
      }
    } catch (error) {
      console.log('error', error);
      if (typeof onEndLtPlusSignup === 'function') {
        onEndLtPlusSignup();
      }
      setIsSubmitting(false);
      resetSubmitProgress();
      setErrorMessage(intl.formatMessage({ id: 'StripePaymentForm.genericError' }));
      log.error(error, 'stripe-checkout-form-submit-failed');
    }
  };

  const messagePlaceholder = intl.formatMessage(
    { id: 'StripePaymentForm.messagePlaceholder' },
    { name: authorDisplayName }
  );

  const changePaymentMethod = changedTo => {
    setPaymentMethod(changedTo);
  };

  const showPaymentMethodSelector = ensuredDefaultPaymentMethod.id;

  const cardClasses = classNames(css.card);

  const { last4Digits } = defaultPaymentMethod?.attributes?.card || {};
  const labelText = intl.formatMessage(
    { id: 'StripePaymentForm.replaceAfterOnetimePayment' },
    { last4Digits }
  );

  const isLtPlusSelected = isGlpLtPlusSelection(guestLiabilityProtection);
  const formattedMembershipPrice = formatLtPlusMonthlyPrice(intl);
  const buttonMessage = isLtPlusSelected
    ? intl.formatMessage({ id: 'StripePaymentForm.subscribeAndSendBooking' })
    : buttonLabel || intl.formatMessage({ id: 'StripePaymentForm.sendBooking' });
  const totalPrice = get(transaction, 'attributes.payinTotal', null);
  const formattedTotalPriceNumber = totalPrice && convertMoneyToNumber(totalPrice);
  const listingId = listing?.id?.uuid;
  const userId = user?.id?.uuid;
  const followProperty = () => {
    if (userId) {
      followSubjectMutation(userId, listingId, { sms: true, email: true });
    }
  };

  return (
    <>
      <CheckoutSubmitOverlay phase={submitProgressPhase} includeLtPlus={includeLtPlusSubmitFlow} />

      <FinalForm
        onSubmit={handleFormSubmit}
        keepDirtyOnReinitialize
        initialValues={{
          ...(guestLiabilityProtection !== undefined && { guestLiabilityProtection }),
          ...(typeof tripInsurance === 'boolean' && { tripInsurance }),
        }}
        render={fieldRenderProps => {
          const { handleSubmit } = fieldRenderProps;

          return (
            <Form id="payment-form" className={css.paymentForm} onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ submitFailed: true }}
                onChange={() => {
                  if (submitProgressPhaseRef.current !== CHECKOUT_SUBMIT_PHASE.IDLE) {
                    return;
                  }

                  setTimeout(() => {
                    // Look for specific field errors first, then fall back to generic validation-error
                    const firstErrorElement =
                      document.querySelector('[data-field="guestLiabilityProtection"]') ||
                      document.querySelector('[data-field="tripInsurance"]') ||
                      document.querySelector('.validation-error');
                    if (firstErrorElement) {
                      firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 0);
                }}
              />

              <CredovaPlugin price={formattedTotalPriceNumber} variant="large" />

              <h3 className={css.formHeading}>
                <FormattedMessage id="StripePaymentForm.paymentHeadline" />
              </h3>
              <div className={css.formSubcopy}>
                <FormattedMessage id="StripePaymentForm.paymentSubcopy" />
              </div>
              {showPaymentMethodSelector ? (
                <div>
                  <PaymentMethodSelector
                    cardClasses={cardClasses}
                    formId="StripeCheckoutForm"
                    defaultPaymentMethod={ensuredDefaultPaymentMethod}
                    changePaymentMethod={changePaymentMethod}
                    intl={intl}
                  />

                  {paymentMethod !== 'defaultCard' && (
                    <PaymentForm
                      intl={intl}
                      label={paymentMethod === 'replaceCard' && labelText}
                      showSaveCardCheckbox={showSaveCardCheckbox}
                    />
                  )}
                </div>
              ) : (
                <PaymentForm intl={intl} showSaveCardCheckbox={showSaveCardCheckbox} />
              )}

              {/* Show any error or success messages */}
              {paymentMessage && (
                <div id="payment-message" className={css.paymentMessage}>
                  {paymentMessage}
                </div>
              )}

              {errorMessage && (
                <div id="error-message" className={css.errorMessage}>
                  {errorMessage}
                </div>
              )}

              {onApplyPromoCode && (
                <FieldPromoCode
                  onApply={onApplyPromoCode}
                  usedPromoCode={usedPromoCode}
                  promoCodeApplied={isPromoCodeApplied(transaction)}
                />
              )}

              {onApplyInsurance && showInsuranceForm && (
                <>
                  <FieldTripInsurance
                    onApply={onApplyInsurance}
                    intl={intl}
                    transaction={transaction}
                    isApplyingInsurance={isApplyingInsurance}
                    setIsApplyingInsurance={setIsApplyingInsurance}
                  />
                </>
              )}

              {onApplyGuestLiabilityProtection && showGuestLiabilityProtectionForm && (
                <FieldGuestLiabilityProtection
                  onApply={onApplyGuestLiabilityProtection}
                  transaction={transaction}
                  isLocked={isGuestLiabilityProtectionLocked}
                  isApplyingProtection={isApplyingGuestLiabilityProtection}
                  setIsApplyingProtection={setIsApplyingGuestLiabilityProtection}
                />
              )}

              {showInitialMessageInput ? (
                <div className={css.messageSection}>
                  <Divider my="var(--spacing-5)" />
                  <h3 className={css.formHeading}>
                    <FormattedMessage
                      id="StripePaymentForm.messageHeading"
                      values={{ landowner: authorDisplayName }}
                    />
                  </h3>
                  <div className={css.formSubheading}>
                    <FormattedMessage
                      id="StripePaymentForm.messageSubheading"
                      values={{ landowner: authorDisplayName }}
                    />
                  </div>

                  <FieldTextInput
                    type="textarea"
                    id="StripeCheckoutForm-message"
                    name="initialMessage"
                    placeholder={messagePlaceholder}
                    className={css.message}
                    charLimit={MAX_MESSAGE_LENGTH}
                    validate={composeValidators(
                      required(intl.formatMessage({ id: 'StripePaymentForm.messageRequired' })),
                      maxLength(
                        intl.formatMessage(
                          { id: 'SendMessageForm.maxLength' },
                          {
                            maxLength: MAX_MESSAGE_LENGTH,
                          }
                        ),
                        MAX_MESSAGE_LENGTH
                      )
                    )}
                  />

                  <div className={css.cancelDisclaimer}>
                    <FormattedHTMLMessage id="StripePaymentForm.cancelDisclaimer" />
                  </div>
                </div>
              ) : null}

              <div className={css.bookingBreakdownContainerMobile}>{bookingBreakdown}</div>

              <div className={css.submitContainer}>
                <div className={submitClassName}>
                  <Button
                    type="submit"
                    variant={isLtPlusSelected ? 'plus' : 'primary'}
                    fullWidth
                    size="lg"
                    disabled={
                      isSubmitting ||
                      !stripe ||
                      !elements ||
                      isApplyingInsurance ||
                      isApplyingGuestLiabilityProtection
                    }
                    isLoading={isSubmitting}
                    id="submit"
                    className={css.button}
                    onClick={followProperty}
                  >
                    {buttonMessage}
                  </Button>
                </div>
                {isLtPlusSelected ? (
                  <p className={css.sendBookingDisclaimer}>
                    <FormattedMessage
                      id="StripePaymentForm.subscribeAndSendBookingDisclaimer"
                      values={{
                        membershipPrice: formattedMembershipPrice,
                      }}
                    />
                  </p>
                ) : (
                  <p className={css.sendBookingDisclaimer}>
                    <FormattedMessage id="StripePaymentForm.sendBookingDisclaimer" />
                  </p>
                )}
              </div>
            </Form>
          );
        }}
      />

      <PageSlider
        active={showMissingInformationReminder}
        onClose={() => {
          setShowMissingInformationReminder(false);
        }}
        width="50%"
      >
        <IDVerification
          className={css.innerIDModal}
          onClose={() => {
            setShowMissingInformationReminder(false);
          }}
          showMissingInformationReminder={showMissingInformationReminder}
        />
      </PageSlider>
    </>
  );
};

export default injectIntl(StripeCheckoutForm);
